import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import useItem from 'common/hooks/useItem';
import ImageFromStrapiMedia from 'common/components/ImageFromStrapiMedia/ImageFromStrapiMedia';
import useRouter from 'common/hooks/use-router';
import DetailHeader from 'common/components/DetailHeader/DetailHeader';
import HeaderButtons from 'common/components/DetailHeader/HeaderButtons';
import SectionTitle from 'common/components/SectionTitle/SectionTitle';
import PageLoading from 'common/components/PageLoading/PageLoading';
import ImageComponent from 'common/components/ImageComponent/ImageComponent';
import Icon from 'components/Icon/Icon';
import Status from 'common/components/Status/Status';
import AlertsContext from 'common/providers/alerts';
import ProfileCard from 'components/ProfileCard/ProfileCard';
import 'photoswipe/dist/photoswipe.css';
import { Profile } from 'types/Profile';
import useList from 'common/hooks/useList';
import { Payment } from 'types/Payment';
import { SelectableOptionsList } from 'common/components/SelectableOptionsList/SelectableOptionsList';
import formatPrice from 'utils/formatPrice';

function SubscriptionDetails() {
  const router = useRouter();
  const id = router.query.id as string;
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const [yearToView, setYearToView] = useState<number>(dayjs().year());

  const { item, isFetching, isUpdateFetching, error }: any = useItem('profiles', id, {
    /* fields: [
      "isSubscribed",
      "subscriptionDate",
      "subscriptionEndedDate",
      "premiumValidUntilDate",
      "lastPayementAttempt",
      "partialCardNumber",
      "cardExpirationDate",
      ], */
    populate: ['avatar', 'companyLogo', 'user', 'user.role'],
  });

  useEffect(() => {
    if (error) {
      setAlert(error, 'danger');
    }
  }, [error, setAlert]);

  const {
    username,
    firstname,
    lastname,
    avatar,
    isSubscribed,
    subscriptionDate,
    subscriptionEndedDate,
    premiumValidUntilDate,
    lastPayementAttempt,
    partialCardNumber,
    cardExpirationDate,
  } = (item as Profile) || {};

  const yearList = useMemo(() => {
    const nbYears = dayjs().year() - dayjs(subscriptionDate).year();
    // get an array of years in reverse order between today's year and subscription year
    return Array.from({ length: nbYears + 1 }, (_, i) => dayjs().year() - i);
  }, [subscriptionDate]);

  const { items: payments } = useList<Payment>(`payments/${id}/my_payments/${yearToView}`, {
    defaultSorts: ['executionDate:DESC'],
    listenToEvents: ['invoiceGenerated'],
  });

  const isPremium = dayjs(premiumValidUntilDate).isAfter(dayjs().startOf('day'));

  const PublicationHeaderInfos = useMemo(
    () => (
      <div className="w-full">
        <div className="flex items-center text-xs gap-1 field-title">
          <Icon name="ClockIcon" className="w-4 h-4" />
          {`${dayjs(subscriptionDate).fromNow(true)}`}
        </div>
        <div className="responsive-flex items-center gap-3">
          <div className="flex flex-row items-center text-ternary-800">
            <div className="text-sm flex">
              <div className="flex items-center">
                <span>
                  {t('common.subscribedSince', {
                    since: dayjs(subscriptionDate).format('LL'),
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
    [subscriptionDate, t]
  );

  const PublicationHeaderSubTitle = useCallback(
    (mainText: string, subText?: string) => (
      <>
        <span className="font-bold">{mainText}</span>
        {subText && <span> | {subText}</span>}
      </>
    ),
    []
  );

  const handleAuthorClick = () => {
    if (id) {
      router.navigate(`/profiles/edit/${id}`);
    }
  };

  if (isFetching && !item) {
    return <PageLoading />;
  }

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
      <DetailHeader
        backLink="/subscriptions"
        title={`${username} - ${firstname} ${lastname}`}
        Status={
          <div className="flex gap-2 flex-wrap">
            <Status status={isPremium ? 'premium' : 'standard'} />
          </div>
        }
        subtitleIcon="Subscription"
        TitleIcon={
          <div className="rounded-lg overflow-hidden">
            <ImageComponent image={avatar ? ImageFromStrapiMedia(avatar)?.uri : undefined} />
          </div>
        }
        HeaderInfos={PublicationHeaderInfos}
        SubTitle={PublicationHeaderSubTitle(t('common.subscription'))}
        HeaderButtons={
          <HeaderButtons
            editUrl={`/subscriptions/edit/${id}`}
            // TODO : ne sert plus à rien puisque géré par des routes customs (!=update)
            // Il faudrait intégrer les fetchJSON dans un provider pour gérer les chargements de changement de statut
            isPublishing={isUpdateFetching('updateStatus')}
          />
        }
      />

      <div className="responsive-flex gap-2 mt-2">
        <div className="flex-1">
          <div className="flex flex-col gap-2 w-full">
            {/* Description */}
            <div className="responsive-flex flex flex-col xl:flex-row gap-2 items-start">
              {/* Column 1 */}
              <div className="responsive-flex gap-2 flex-col w-full xl:w-2/3">
                <div className="white-box flex gap-2 flex-col w-full">
                  <SectionTitle
                    title={t('forms.details')}
                    icon="BookmarkIcon"
                    className="text-secondary mt-2"
                  />
                  <div className="flex flex-row gap-4">
                    <div className="flex flex-col gap-2 w-1/2">
                      {isSubscribed ? (
                        <div className="font-bold">
                          {t('common.premiumFormulaSince', {
                            since: dayjs(subscriptionDate).format('DD/MM/YYYY'),
                          })}
                        </div>
                      ) : (
                        <>
                          <div className="">
                            {t('common.premiumFormulaThe', {
                              since: dayjs(subscriptionDate).format('DD/MM/YYYY'),
                            })}
                          </div>
                          <div className="font-bold ">
                            {t('common.subscriptionCancelledThe', {
                              since: dayjs(subscriptionEndedDate).format('DD/MM/YYYY'),
                            })}
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 w-1/2">
                      <div className="flex flex-row justify-end font-bold">
                        <Status status={isPremium ? 'premium' : 'standard'} />
                      </div>
                    </div>
                  </div>

                  <SectionTitle
                    title={t('common.paymentInfo')}
                    icon="CreditCardIcon"
                    className="text-secondary mt-6"
                  />
                  {isSubscribed ? (
                    <div className="flex flex-row gap-4 items-center group-hover:shadow-md my-4">
                      <div className="flex flex-row gap-0 items-center justify-start pl-0 pr-2 h-7 overflow-hidden bg-lightest border border-darkest border-opacity-60 rounded-md hover:shadow-lg hover:border-2">
                        <div className="flex flex-row items-center px-1 h-full bg-darkest bg-opacity-60">
                          <Icon name="CreditCardIcon" className="w-6 h-6" />
                        </div>
                        {partialCardNumber?.match(/.{1,4}/g)?.join(' ')}
                      </div>
                      {`Exp. ${cardExpirationDate}`}
                    </div>
                  ) : (
                    <div className="italic">{t('common.noPaymentInfo')}</div>
                  )}
                </div>
                <div className="white-box flex gap-2 flex-col w-full">
                  <SectionTitle
                    title={t('common.invoices')}
                    icon="CreditCardIcon"
                    className="text-secondary mt-4"
                  />
                  <div>{`Dernière tentative de paiement le ${dayjs(lastPayementAttempt).format(
                    'DD/MM/YYYY'
                  )}`}</div>
                  <SelectableOptionsList
                    options={yearList.map((y: number) => ({
                      value: y,
                      label: String(y),
                    }))}
                    className="flex flex-row-reverse w-full justify-center mt-2 flex-wrap gap-2"
                    value={yearToView}
                    onChange={(val: any) => setYearToView(val)}
                  />
                  {payments && payments.length > 0 ? (
                    payments?.map((payment: any) => (
                      <div
                        key={payment.id}
                        className="flex flex-col gap-1 mt-2 border border-slate-300 bg-slate-50 rounded-md px-4 py-2"
                      >
                        <div className="flex flex-row items-center justify-between">
                          <div className="font-bold text-lg">
                            {dayjs(payment.executionDate).format('DD/MM/YYYY')}
                          </div>
                          <Status status={payment.status} />
                        </div>
                        <div className="flex flex-row items-center justify-end">
                          <div className="flex-1">{`${payment.description} du ${dayjs(
                            payment.effectiveStartDate
                          ).format('DD/MM/YYYY')} au ${dayjs(payment.effectiveEndDate).format(
                            'DD/MM/YYYY'
                          )}`}</div>
                          <div className="font-bold text-lg">
                            {formatPrice(payment.amount / 100)}
                          </div>
                        </div>
                        <a
                          href={`${import.meta.env.VITE_API_URL}${payment.invoice?.url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex flex-row"
                        >
                          <div className="flex flex-row items-center mt-1 -ml-0.5 gap-2 text-blue-600 border border-blue-600 bg-blue-50 rounded-full px-2 hover:bg-slate-200">
                            <Icon name="DownloadFile" className="w-4 h-4" />
                            {t('common.invoice')}
                          </div>
                        </a>
                      </div>
                    ))
                  ) : (
                    <div className="italic w-full text-center mt-4">
                      {t('common.noInvoices', { year: yearToView })}
                    </div>
                  )}
                </div>
              </div>
              {/* Column 2 */}
              <div className="white-box responsive-flex gap-4 flex-col w-full lg:basis-1/2">
                <ProfileCard profile={item} title={t('forms.author')} onClick={handleAuthorClick} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriptionDetails;
